// Session lifetime on the client. The access token lives 2 h; on a 401 the request is retried once after
// POST /auth/refresh (the refresh token ends 12 h after login, then the user signs in again).
// IDLE_MS without a tap, click, key or wheel signs this device out. The last activity time is shared
// through localStorage, so one active tab keeps the other tabs alive, and a reload does not reset it.
// The axios client is passed in, so the jest tests can use a fake one.
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const IDLE_MS = 120 * 60_000;
const CHECK_MS = 60_000;
const ACTIVITY_KEY = "boxprep.lastActivity";
const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout"];

export function installSessionRefresh(client, onExpired) {
  let refreshing = null;
  const id = client.interceptors.response.use(undefined, async (error) => {
    const config = error.config;
    if (error.response?.status !== 401 || !config || config._retried || NO_REFRESH.some(p => config.url?.includes(p))) {
      throw error;
    }
    // Parallel 401s wait for the same refresh call.
    refreshing ||= client.post(`${API}/auth/refresh`, {}, { withCredentials: true }).finally(() => { refreshing = null; });
    try {
      await refreshing;
    } catch {
      onExpired();
      throw error;
    }
    return client({ ...config, _retried: true });
  });
  return () => client.interceptors.response.eject(id);
}

const readActivity = () => {
  try { return Number(localStorage.getItem(ACTIVITY_KEY)) || 0; } catch { return 0; }
};

export function markActive(now = Date.now()) {
  try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* private mode: this tab's timer still works */ }
  return now;
}

export function installIdleLogout(onIdle, now = Date.now) {
  // A missing value means no login was recorded here yet; start counting now.
  let last = readActivity() || markActive(now());
  const touch = () => {
    if (now() - last > 10_000) last = markActive(now());   // throttle storage writes
  };
  const check = () => {
    last = Math.max(last, readActivity());
    if (now() - last >= IDLE_MS) onIdle();
  };
  const events = ["pointerdown", "keydown", "wheel"];
  events.forEach(e => window.addEventListener(e, touch, { passive: true }));
  const timer = setInterval(check, CHECK_MS);
  check();
  return () => {
    events.forEach(e => window.removeEventListener(e, touch));
    clearInterval(timer);
  };
}
